import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PackageCheck, AlertTriangle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const inp = "h-11 w-full px-3 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B] font-mono text-sm";

function DeliveredPrompt({ n, onDone }) {
  const nav = useNavigate();
  const close = async (review) => {
    await api.post(`/po-alerts/deliveries/${n.id}/dismiss`).catch(() => null);
    onDone();
    if (review) nav(`/ordering?po=${n.id}&filter=all`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close(false)}>
      <DialogContent className="bg-[#0F1623] border-[#10B981] max-w-md" data-testid="delivery-prompt">
        <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2 text-[#10B981]"><PackageCheck className="w-5 h-5" />Shipment delivered</DialogTitle></DialogHeader>
        <p className="text-sm text-[#9CA3AF]" data-testid="delivery-prompt-text">The carrier marked <b className="text-white">{n.po_number}</b>{n.supplier?.name ? ` from ${n.supplier.name}` : ""} as delivered{n.tracking_number ? ` (tracking ${n.tracking_number})` : ""}. Review it and receive the stock when it's checked in.</p>
        <div className="flex gap-2">
          <button data-testid="delivery-prompt-review-btn" onClick={() => close(true)} className="flex-1 h-12 rounded-md bg-[#10B981] text-[#090D16] font-bold">Review PO</button>
          <button data-testid="delivery-prompt-cancel-btn" onClick={() => close(false)} className="h-12 px-5 rounded-md border border-[#334155]">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VariancePrompt({ v, onDone }) {
  const [dates, setDates] = useState({ sent: v.dates.sent || "", received: v.dates.received || "" });
  const [busy, setBusy] = useState(false);
  const f = v.flag;
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const changed = dates.sent !== (v.dates.sent || "") || dates.received !== (v.dates.received || "");
  return (
    <Dialog open onOpenChange={() => null}>
      <DialogContent className="bg-[#0F1623] border-[#F59E0B] max-w-lg [&>button]:hidden" data-testid="variance-prompt">
        <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2 text-[#F59E0B]"><AlertTriangle className="w-5 h-5" />Check delivery dates · {v.po_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#9CA3AF]" data-testid="variance-prompt-text">{v.supplier || "This supplier"} took <b className="text-white">{f.actual_days} days</b> (Sent → Delivered) against an average of <b className="text-white">{f.expected_days} days</b>: <b className="text-[#F59E0B]">{f.variance_pct}% off</b>, over your {f.threshold_pct}% limit. Confirm the dates are right before they feed the supplier's lead time.</p>
        <div className="grid grid-cols-2 gap-3">
          {["sent", "received"].map((k) => <label key={k} className="flex flex-col gap-1"><span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">{k.toUpperCase()} DATE</span>
            <input type="date" data-testid={`variance-date-${k}`} value={dates[k]} onChange={(e) => setDates({ ...dates, [k]: e.target.value })} className={inp} /></label>)}
        </div>
        <div className="grid gap-2">
          {changed ? <button data-testid="variance-fix-btn" disabled={busy} onClick={() => act(() => api.put(`/purchase-orders/${v.id}`, { checkpoint_dates: dates }), "Dates corrected")} className="h-12 rounded-md bg-[#F59E0B] text-[#090D16] font-bold disabled:opacity-40">Save corrected dates</button>
            : <button data-testid="variance-confirm-btn" disabled={busy} onClick={() => act(() => api.post(`/po-alerts/variance/${v.id}`, { action: "confirm" }), "Dates confirmed")} className="h-12 rounded-md bg-[#F59E0B] text-[#090D16] font-bold disabled:opacity-40">Dates are correct</button>}
          <button data-testid="variance-exclude-btn" disabled={busy} onClick={() => act(() => api.post(`/po-alerts/variance/${v.id}`, { action: "exclude" }), "Left out of supplier history")} className="h-12 rounded-md border border-[#334155] text-sm font-semibold disabled:opacity-40">Leave this PO out of supplier history</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function POAlertPrompts() {
  const [del, setDel] = useState([]);
  const [vari, setVari] = useState([]);
  const load = useCallback(() => {
    api.get("/po-alerts/deliveries").then((r) => setDel(r.data)).catch(() => null);
    api.get("/po-alerts/variance").then((r) => setVari(r.data)).catch(() => null);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (del[0]) return <DeliveredPrompt key={del[0].id} n={del[0]} onDone={load} />;
  if (vari[0]) return <VariancePrompt key={vari[0].id + vari[0].flag.flagged_at} v={vari[0]} onDone={load} />;
  return null;
}
